<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';

interface IStoreProduct {
  id: string;
  designation: string | { fr?: string; en?: string; nl?: string };
}

const props = defineProps<{
  storeId: string;
  modelValue: string[];
}>();

const emit = defineEmits<{
  'update:modelValue': [productIds: string[]];
}>();

const PAGE_SIZE = 10;
const search = ref('');
const page = ref(1);

watch(search, () => {
  page.value = 1;
});

const { data: response, isPending } = useQuery({
  queryKey: computed(() => [
    'campaign-target-products',
    props.storeId,
    page.value,
    search.value,
  ]),
  queryFn: async () => {
    const params = new URLSearchParams({
      restau: props.storeId,
      pagination: 'true',
      page: String(page.value),
      pageSize: String(PAGE_SIZE),
    });
    if (search.value.trim()) params.set('q', search.value.trim());
    const r = await axiosInstance.get(`products/search?${params}`);
    return r.data as {
      data: IStoreProduct[];
      total: number;
      page: number;
      pageSize: number;
    };
  },
  enabled: computed(() => !!props.storeId),
});

const products = computed(() => response.value?.data ?? []);
const total = computed(() => response.value?.total ?? 0);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(total.value / PAGE_SIZE)),
);

function productName(p: IStoreProduct): string {
  if (typeof p.designation === 'string') return p.designation;
  return p.designation?.fr || p.designation?.en || p.designation?.nl || '—';
}

function isSelected(id: string): boolean {
  return props.modelValue.includes(id);
}
function toggle(id: string) {
  const next = isSelected(id)
    ? props.modelValue.filter((v) => v !== id)
    : [...props.modelValue, id];
  emit('update:modelValue', next);
}

function goToPage(target: number) {
  if (target < 1 || target > totalPages.value) return;
  page.value = target;
}
</script>

<template>
  <div class="ctp">
    <div class="ctp__toolbar">
      <input
        v-model="search"
        class="ctp__search"
        type="text"
        placeholder="Rechercher un produit…"
      />
      <span class="ctp__count">{{ modelValue.length }} sélectionné(s)</span>
    </div>

    <div v-if="isPending" class="ctp__loading">Chargement…</div>
    <div v-else-if="!products.length" class="ctp__empty">
      Aucun produit trouvé.
    </div>
    <ul v-else class="ctp__list">
      <li v-for="p in products" :key="p.id" class="ctp__item">
        <label class="ctp__label">
          <span class="ctp__checkbox">
            <input
              type="checkbox"
              :checked="isSelected(p.id)"
              @change="toggle(p.id)"
            />
            <span class="ctp__checkbox-box">
              <i class="pi pi-check" />
            </span>
          </span>
          <span>{{ productName(p) }}</span>
        </label>
      </li>
    </ul>

    <div v-if="total > PAGE_SIZE" class="ctp__pagination">
      <button
        type="button"
        class="ctp__page-btn"
        :disabled="page <= 1"
        @click="goToPage(page - 1)"
      >
        <i class="pi pi-chevron-left" />
      </button>
      <span class="ctp__page-info">Page {{ page }} / {{ totalPages }}</span>
      <button
        type="button"
        class="ctp__page-btn"
        :disabled="page >= totalPages"
        @click="goToPage(page + 1)"
      >
        <i class="pi pi-chevron-right" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.ctp {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.ctp__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.ctp__search {
  flex: 1;
  min-width: 12rem;
  height: 2.25rem;
  padding: 0 0.75rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-family: inherit;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}
.ctp__search:focus {
  outline: none;
  border-color: var(--nxt-primary);
  box-shadow: 0 0 0 3px rgba(var(--nxt-primary-rgb, 246, 132, 2), 0.15);
}
.ctp__count {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
}
.ctp__loading,
.ctp__empty {
  padding: 1.5rem 0.5rem;
  text-align: center;
  color: var(--nxt-on-surface-muted);
  font-size: 0.8125rem;
}
.ctp__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 16rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  padding: 0.375rem;
}
.ctp__item {
  border-radius: 0.5rem;
}
.ctp__label {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.625rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface);
  cursor: pointer;
  border-radius: 0.5rem;
}
.ctp__label:hover {
  background: var(--nxt-surface-mid);
}
.ctp__checkbox {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}
.ctp__checkbox input {
  position: absolute;
  inset: 0;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}
.ctp__checkbox-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 0.3rem;
  border: 1.5px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: transparent;
  font-size: 0.6875rem;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
}
.ctp__checkbox input:checked ~ .ctp__checkbox-box {
  background: var(--nxt-primary);
  border-color: var(--nxt-primary);
  color: #fff;
}
[data-theme='dark'] .ctp__checkbox input:checked ~ .ctp__checkbox-box {
  background: #ffd60a;
  border-color: #ffd60a;
  color: #0b1320;
}
.ctp__checkbox input:focus-visible ~ .ctp__checkbox-box {
  outline: 2px solid var(--nxt-primary);
  outline-offset: 1px;
}
.ctp__pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}
.ctp__page-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
}
.ctp__page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.ctp__page-info {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}
</style>
